import {
  Box,
  Button,
  MenuItem,
  Typography,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocationSearch from "../Component/LocationSearch";
import { useEffect, useState } from "react";
import ApiEndpoint from "../../config/ApiEndpoint";
import ApiServices from "../../config/ApiServices";
import { toast } from "react-toastify";
import { PageHeader, Card, DataTable, StatusBadge, SearchInput, ActionButton, UserDetailsModal, TouchFriendlyTooltip } from "../shared";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Constants from "../../config/Constants";
import { formatClubType } from "../../Utils/displayLabels";
import { reverseGeocodeToSuburb, filterActiveMasterOrgMembers } from "../../Utils/func";

// Simple in-memory cache to avoid repeated reverse-geocoding calls
const masterOrgLocationCache = {};

const UsersDirectory = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const PAGE_LIMIT = 10;
  const token = props?.props?.profile?.token;
  const userType =
    props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sportsList, setSportsList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_LIMIT);
  const [totalUsers, setTotalUsers] = useState(0);
  // Admin-controlled flag: whether this club partner may see member emails.
  // Seed from profile (loaded in AppLayout) and refresh from the users response.
  const profileAllowEmails =
    props?.props?.profile?.allow_member_emails ??
    props?.props?.profile?.data?.allow_member_emails ??
    false;
  const [allowMemberEmails, setAllowMemberEmails] = useState(profileAllowEmails);
  const [filters, setFilters] = useState({
    role: "",
    status: "active",
    sport_id: "",
    sub_org_id: "",
    search: "",
    location: "",
    user_latitude: "",
    user_longitude: "",
    radius: "",
  });
  const [subOrgsList, setSubOrgsList] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [subOrgsDialog, setSubOrgsDialog] = useState({ open: false, user: null, subOrgs: [] });
  const [sportsDialog, setSportsDialog] = useState({ open: false, sports: [], user: null });
  const [userDetailsModal, setUserDetailsModal] = useState({
    open: false,
    userId: null,
    role: null,
    locationLabel: "",
  });

  const loadSports = async () => {
    if (!token) return;
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.SPORT_LIST_COMMON,
        {},
        headers
      );
      if (data?.status && Array.isArray(data.data)) {
        setSportsList(data.data);
      }
    } catch (error) {
      console.error("Failed to load sports:", error);
    }
  };

  const loadSubOrgs = async () => {
    if (!token) return;
    try {
      const headers = {
        "Content-Type": "application/json",
        "x-access-token": token,
      };
      const data = await ApiServices.GetApiCall(ApiEndpoint.MASTER_ORG_SUB_ORGS, headers);
      if (data?.status && Array.isArray(data.data)) {
        setSubOrgsList(data.data);
      }
    } catch (error) {
      console.error("Failed to load sub-orgs:", error);
    }
  };

  const buildRequestBody = (pageNum = null) => {
    const body = {
      type: "user",
      pagination: true,
      page: (pageNum !== null ? pageNum : page) + 1, // Backend uses 1-indexed pages
      limit: rowsPerPage,
      srch: filters.search || "", // Note: backend expects "srch" not "search"
    };
    
    // Only send sport_id if explicitly selected in filter
    // Backend now supports post-query filtering, so empty sport_id shows all sports
    if (filters.sport_id) {
      const sportIdNum = parseInt(filters.sport_id, 10);
      if (!isNaN(sportIdNum) && sportIdNum > 0) {
        body.sport_id = sportIdNum;
      }
    }
    
    // Filter by role if specified
    if (filters.role && !["schools", "individual", "master_org", "sub_org", "master"].includes(filters.role)) {
      body.role = filters.role;
    }
    
    // Filter by status — default active (matches org website member list)
    if (filters.status) {
      body.status = filters.status;
    }
    
    // Location filters (require both lat and lng) - only send if explicitly set
    if (filters.user_latitude && filters.user_longitude) {
      body.user_latitude = parseFloat(filters.user_latitude);
      body.user_longitude = parseFloat(filters.user_longitude);
      body.radius = parseFloat(filters.radius || "1000");
    }

    // Filter by sub-organization (users in that sub-org)
    if (filters.sub_org_id) {
      const subOrgIdNum = parseInt(filters.sub_org_id, 10);
      if (!isNaN(subOrgIdNum) && subOrgIdNum > 0) {
        body.sub_org_id = subOrgIdNum;
      }
    }

    return body;
  };
  
  useEffect(() => {
    if (userType === "master_org" && token) {
      loadSports();
      loadSubOrgs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, token]);

  // Reset to page 0 when filters change
  useEffect(() => {
    if (userType === "master_org" && token) {
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, token, filters.role, filters.status, filters.sport_id, filters.sub_org_id, filters.search, filters.location]);

  useEffect(() => {
    if (userType === "master_org" && token) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, token, filters, page, rowsPerPage]);

  const loadUsers = async () => {
    if (!token) return;
    setLoading(true);
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    props.setIsLoaded(true);
    try {
      // Master org endpoint now uses POST with request body (same as admin)
      const requestBody = buildRequestBody();
      
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_USERS,
        JSON.stringify(requestBody),
        headers
      );
      props.setIsLoaded(false);
      setLoading(false);
      
      if (data?.status) {
        // Response format: { status: true, data: [...], count: 100, cuurentPage: 1, totalPage: 10 }
        // Backend returns flat array in data.data (same as admin)
        // Backend includes allow_member_emails to control whether emails are exposed.
        if (data.allow_member_emails !== undefined) {
          setAllowMemberEmails(data.allow_member_emails);
        }
        let allData = Array.isArray(data.data) ? data.data : [];
        
        // Debug: Log first user to see API structure
        if (allData.length > 0 && process.env.NODE_ENV === 'development') {
          console.log('Sample user data structure:', allData[0]);
          console.log('Sub-org related fields (should be ARRAY):', {
            sub_orgs: allData[0].sub_orgs,
            sub_org: allData[0].sub_org,
            subOrgs: allData[0].subOrgs,
            subOrg: allData[0].subOrg,
            organisations: allData[0].organisations,
            organisations_details: allData[0].organisations_details,
            organizations: allData[0].organizations,
            organizations_details: allData[0].organizations_details,
            id_organisation: allData[0].id_organisation,
            organisation_id: allData[0].organisation_id,
          });
        }
        
        // Active members only by default; client-side filter as safety net
        const usersOnly = filters.status
          ? allData.filter(
              (item) =>
                !["schools", "individual", "master_org", "sub_org", "master"].includes(item.role) &&
                item.status === filters.status
            )
          : filterActiveMasterOrgMembers(allData);

        // Backend handles location filtering - no client-side filtering needed
        setUsers(usersOnly);
        // Set total count for pagination
        setTotalUsers(data.count ?? usersOnly.length);
      } else if (data?.message) {
        toast.error(data.message);
        setUsers([]);
        setTotalUsers(0);
      }
    } catch (error) {
      props.setIsLoaded(false);
      setLoading(false);
      toast.error("Failed to load users");
      setUsers([]);
    }
  };


  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleLocationSelect = (e, locationData) => {
    if (locationData) {
      setFilters(prev => ({
        ...prev,
        location: locationData.address || prev.location,
        user_latitude: locationData.lat?.toString() || "",
        user_longitude: locationData.lng?.toString() || "",
      }));
    } else {
      handleFilterChange('location', e.target.value);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      role: "",
      status: "active",
      sport_id: "",
      sub_org_id: "",
      search: "",
      location: "",
      user_latitude: "",
      user_longitude: "",
      radius: "",
    });
  };

  const hasActiveFilters =
    filters.role ||
    (filters.status && filters.status !== "active") ||
    filters.sport_id ||
    filters.sub_org_id ||
    filters.search ||
    filters.location;

  // Export users to CSV
  const handleExport = async () => {
    if (!token) return;
    
    // Warn if current list is empty and filters are applied
    const currentUserCount = totalUsers || users.length;
    if (hasActiveFilters && currentUserCount === 0) {
      const confirm = window.confirm(
        `No users found matching the current filters. The export will be empty.\n\n` +
        `Active filters:\n` +
        `${filters.role ? `- Role: ${filters.role}\n` : ''}` +
        `${filters.status ? `- Status: ${filters.status}\n` : ''}` +
        `${filters.sport_id ? `- Sport: ${sportsList.find(s => s.id.toString() === filters.sport_id)?.name || filters.sport_id}\n` : ''}` +
        `${filters.sub_org_id ? `- Club: ${subOrgsList.find(s => s.id.toString() === filters.sub_org_id)?.name || filters.sub_org_id}\n` : ''}` +
        `${filters.search ? `- Search: ${filters.search}\n` : ''}` +
        `${filters.location ? `- Location: ${filters.location}\n` : ''}` +
        `\nDo you want to continue with the export?`
      );
      if (!confirm) {
        return;
      }
    }
    
    setIsExporting(true);
    
    try {
      // Build query string from current filters (same as list view)
      const queryParams = new URLSearchParams();
      
      // Only send sport_id if explicitly selected in filter
      if (filters.sport_id) {
        queryParams.append('sport_id', filters.sport_id);
      }
      
      if (filters.role) {
        queryParams.append('role', filters.role);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.sub_org_id) {
        queryParams.append('sub_org_id', filters.sub_org_id);
      }
      // Location filters (require both lat and lng)
      if (filters.user_latitude && filters.user_longitude) {
        queryParams.append('user_latitude', filters.user_latitude);
        queryParams.append('user_longitude', filters.user_longitude);
        // Use default 1000 if radius is empty
        queryParams.append('radius', filters.radius || '1000');
      }

      const queryString = queryParams.toString();
      const url = `${ApiEndpoint.MASTER_ORG_USER_EXPORT}${queryString ? '?' + queryString : ''}`;
      
      const headers = {
        "x-access-token": token,
      };

      const { blob, filename } = await ApiServices.GetBlobApiCall(url, headers);
      
      if (!blob) {
        throw new Error('Export failed - no data returned');
      }

      // Modify filename to include location text if location filter is applied
      let finalFilename = filename;
      if (filters.user_latitude && filters.user_longitude && filters.location) {
        // Sanitize location text for filename (remove special chars, replace spaces with underscores)
        const sanitizedLocation = filters.location
          .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
          .replace(/\s+/g, '_') // Replace spaces with underscores
          .substring(0, 50) // Limit length
          .trim();
        
        if (sanitizedLocation) {
          // Insert location before .csv extension
          const nameWithoutExt = finalFilename.replace(/\.csv$/i, '');
          finalFilename = `${nameWithoutExt}_${sanitizedLocation}.csv`;
        }
      }

      // Check if blob is empty (CSV with only headers)
      const blobSize = blob.size;
      
      if (blobSize < 100) {
        // Very small file likely means only headers or empty
        const text = await blob.text();
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length <= 1) {
          toast.warning('Export completed but no data found matching the filters. The CSV may only contain headers.');
        }
      }

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = finalFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`Export successful! File: ${finalFilename} (${(blobSize / 1024).toFixed(2)} KB)`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Export failed. Check console for details.');
    } finally {
      setIsExporting(false);
    }
  };

  if (userType !== "master_org") {
    return (
      <Box>
        <PageHeader title="Members" subtitle="Access denied." />
        <Card>
          <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            This page is for club partner accounts only.
          </Typography>
        </Card>
      </Box>
    );
  }
  
  const LocationCell = ({ row }) => {
    const [label, setLabel] = useState("");

    useEffect(() => {
      // Prefer backend-provided suburb/city (user_suburb set on save from backend geocode)
      const city =
        row.user_suburb ||
        row.city ||
        row.town ||
        row.suburb ||
        row.locality ||
        row.user_city ||
        "";

      if (city) {
        setLabel(city);
        return;
      }

      // If we have an explicit user_address/location string but no city,
      // fall back to showing that once (older data)
      const addressLine =
        row.user_address ||
        row.location ||
        row.address ||
        row.address_line ||
        row.full_address ||
        "";

      if (addressLine) {
        setLabel(addressLine);
        return;
      }

      const lat = parseFloat(row.user_latitude || row.latitude);
      const lng = parseFloat(row.user_longitude || row.longitude);

      if (Number.isNaN(lat) || Number.isNaN(lng)) {
        setLabel("-");
        return;
      }

      const cacheKey = `${lat},${lng}`;
      if (masterOrgLocationCache[cacheKey]) {
        setLabel(masterOrgLocationCache[cacheKey]);
        return;
      }

      let isMounted = true;
      const apiKey = Constants.MAP_API_KEY;

      const fetchLocation = async () => {
        try {
          const display = await reverseGeocodeToSuburb(lat, lng, apiKey);
          masterOrgLocationCache[cacheKey] = display;
          if (isMounted) setLabel(display);
        } catch (error) {
          console.error("Reverse geocoding failed:", error);
          if (isMounted) setLabel(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        }
      };

      fetchLocation();

      return () => {
        isMounted = false;
      };
    }, [row]);

    const handleClick = () => {
      if (!label) return;
      setUserDetailsModal({
        open: true,
        userId: row.id,
        role: row.role,
        locationLabel: label,
      });
    };

    return (
      <Typography
        onClick={handleClick}
        title={label || ""}
        sx={{
          color: isDark ? "#cbd5e1" : "#4a5568",
          fontSize: "0.75rem",
          cursor: label ? "pointer" : "default",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 260,
        }}
      >
        {label || "-"}
      </Typography>
    );
  };

  // Table columns
  const columns = [
    {
      field: "id",
      label: "ID",
      width: 70,
    },
    {
      field: "name",
      label: "User",
      width: 300,
      render: (_, row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={row.profile_image || ""}
            sx={{
              width: 40,
              height: 40,
              bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
              color: isDark ? "#818cf8" : "#1976d2",
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            {row.first_name?.[0] || ""}
            {row.last_name?.[0] || ""}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
              {row.first_name || ""} {row.last_name || ""}
            </Typography>
            {allowMemberEmails && (
              <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
                {row.email || "-"}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: "sports",
      label: "Sports",
      width: 120,
      mobileSameLineGroup: "info",
      render: (_, row) => {
        // Backend exposes a unified sports_list per user:
        // [{ id, name, source: 'primary' | 'organisation' }, ...]
        let sports = [];

        if (Array.isArray(row.sports_list)) {
          sports = row.sports_list
            .map((s) => s?.name || s?.sport_name || s?.title || "")
            .filter(Boolean);
        }

        // Legacy/alternate shapes as fallbacks
        if (!sports.length && Array.isArray(row.sports)) {
          sports = row.sports
            .map((s) => s?.name || s?.sport_name || s?.title || "")
            .filter(Boolean);
        }

        const singleSport =
          row.sport_name ||
          row.sport ||
          row.primary_sport ||
          row.user_sport ||
          "";

        if (!sports.length && singleSport) {
          sports = [singleSport];
        }

        const uniqueSports = [...new Set(sports)];
        if (!uniqueSports.length) {
          return (
            <Typography
              sx={{ color: isDark ? "#cbd5e1" : "#4a5568", fontSize: "0.875rem" }}
            >
              -
            </Typography>
          );
        }

        const visibleSports = uniqueSports.slice(0, 2);
        const remainingCount = uniqueSports.length - visibleSports.length;
        const label = remainingCount > 0
          ? `${visibleSports.join(", ")} +${remainingCount}`
          : visibleSports.join(", ");

        const handleClick = (e) => {
          e.stopPropagation();
          setSportsDialog({ open: true, sports: uniqueSports, user: row });
        };

        return (
          <Typography
            onClick={handleClick}
            title={uniqueSports.join(", ")}
            sx={{
              color: isDark ? "#cbd5e1" : "#4a5568",
              fontSize: "0.75rem",
              cursor: "pointer",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {label}
          </Typography>
        );
      },
    },
    {
      field: "location",
      label: "Location",
      width: 220,
      align: "right",
      mobileSameLineGroup: "info",
      render: (_, row) => <LocationCell row={row} />,
    },
    {
      field: "sub_org",
      label: "Club",
      width: 260,
      mobileInlineWithTitle: true,
      render: (_, row) => {
        // Check various possible field names for sub-org data
        // Backend should return sub-org info as an ARRAY since users can belong to multiple sub-orgs
        let subOrgs = row.sub_orgs || 
                     row.sub_org || 
                     row.subOrgs || 
                     row.subOrg || 
                     row.organisations || 
                     row.organisations_details ||
                     row.organizations || 
                     row.organizations_details ||
                     row.orgs ||
                     row.org_details ||
                     [];
        
        // Handle case where it might be a single object (backward compatibility)
        if (!Array.isArray(subOrgs)) {
          // If it's a single object, convert to array
          if (subOrgs && typeof subOrgs === 'object' && Object.keys(subOrgs).length > 0) {
            subOrgs = [subOrgs];
          } else {
            subOrgs = [];
          }
        }
        
        // Also check if sub-org data is nested in user or other objects
        const subOrgsFromUser = row.user?.organisations || 
                               row.user?.organisations_details ||
                               row.user?.organizations ||
                               row.user?.organizations_details ||
                               [];
        
        if (Array.isArray(subOrgsFromUser) && subOrgsFromUser.length > 0) {
          subOrgs = [...subOrgs, ...subOrgsFromUser];
        } else if (subOrgsFromUser && typeof subOrgsFromUser === 'object') {
          subOrgs = [...subOrgs, subOrgsFromUser];
        }
        
        // Remove duplicates based on sub-org ID
        const uniqueSubOrgs = [];
        const seenIds = new Set();
        
        subOrgs.forEach(subOrg => {
          const orgId = subOrg.id || subOrg.organisation_id || subOrg.organization_id;
          const orgName = subOrg.name || 
                        subOrg.organisation_name || 
                        subOrg.organization_name ||
                        subOrg.org_name ||
                        "";
          
          // Only include if it has a name and we haven't seen this ID before
          if (orgName) {
            if (orgId && !seenIds.has(orgId)) {
              seenIds.add(orgId);
              uniqueSubOrgs.push(subOrg);
            } else if (!orgId) {
              // If no ID, check by name to avoid duplicates
              const nameExists = uniqueSubOrgs.some(org => 
                (org.name || org.organisation_name || org.organization_name || org.org_name) === orgName
              );
              if (!nameExists) {
                uniqueSubOrgs.push(subOrg);
              }
            }
          }
        });
        
        // Debug: Log sub-org data for this user
        if (process.env.NODE_ENV === 'development' && uniqueSubOrgs.length > 0) {
          console.log(`User ${row.first_name} ${row.last_name} (ID: ${row.id}) sub-orgs:`, {
            rawCount: subOrgs.length,
            uniqueCount: uniqueSubOrgs.length,
            subOrgs: uniqueSubOrgs.map(org => ({
              id: org.id,
              name: org.name || org.organisation_name || org.organization_name,
            })),
            rawData: subOrgs,
          });
        }
        
        // If no sub-org data, show "-"
        if (uniqueSubOrgs.length === 0) {
          return (
            <Typography sx={{ color: isDark ? "#718096" : "#718096", fontSize: "0.875rem" }}>
              -
            </Typography>
          );
        }
        
        // Display logos in a row with "+X" indicator (always show max 2 avatars, then "+X")
        const maxVisible = 2;
        const visibleOrgs = uniqueSubOrgs.slice(0, maxVisible);
        const remainingCount = uniqueSubOrgs.length - maxVisible;
        
        return (
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 0.5,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSubOrgsDialog({ open: true, user: row, subOrgs: uniqueSubOrgs });
            }}
          >
            {visibleOrgs.map((subOrg, index) => {
              const logoUrl = subOrg.logo_url || subOrg.logo || "";
              const orgName = subOrg.name || 
                             subOrg.organisation_name || 
                             subOrg.organization_name ||
                             subOrg.org_name ||
                             "";
              
              if (!orgName) return null;
              
              return (
                <Avatar
                  key={subOrg.id || index}
                  src={logoUrl}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                    color: isDark ? "#818cf8" : "#1976d2",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    border: isDark ? "2px solid rgba(255, 255, 255, 0.1)" : "2px solid #ffffff",
                    marginLeft: index > 0 ? "-8px" : 0,
                    zIndex: maxVisible - index,
                    "&:hover": {
                      zIndex: 10,
                      transform: "scale(1.1)",
                      transition: "all 0.2s ease",
                    },
                  }}
                >
                  {orgName[0]?.toUpperCase() || "O"}
                </Avatar>
              );
            })}
            {remainingCount > 0 && (
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(25, 118, 210, 0.15)",
                  color: isDark ? "#818cf8" : "#1976d2",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  border: isDark ? "2px solid rgba(255, 255, 255, 0.1)" : "2px solid #ffffff",
                  marginLeft: "-8px",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: isDark ? "rgba(99, 102, 241, 0.3)" : "rgba(25, 118, 210, 0.25)",
                    transform: "scale(1.1)",
                    transition: "all 0.2s ease",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSubOrgsDialog({ open: true, user: row, subOrgs: uniqueSubOrgs });
                }}
              >
                +{remainingCount}
              </Avatar>
            )}
          </Box>
        );
      },
    },
    {
      field: "role",
      label: "Role",
      mobileSameLineGroup: "meta",
      render: (value) => (
        <Chip
          label={value || "-"}
          size="small"
          sx={{
            backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
            color: isDark ? "#818cf8" : "#1976d2",
            fontWeight: 500,
            fontSize: "0.75rem",
            textTransform: "capitalize",
          }}
        />
      ),
    },
    {
      field: "status",
      label: "Status",
      align: "right",
      mobileSameLineGroup: "meta",
      render: (value) => {
        // Use status directly from API response, same as admin user list
        return <StatusBadge status={value} />;
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Members"
        subtitle="View and search all members in your club"
        breadcrumbs={[{ label: "Club Partner" }, { label: "Members" }]}
      >
        <TouchFriendlyTooltip
          title={
            <Box sx={{ maxWidth: 320 }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                Who appears here?
              </Typography>
              <Typography variant="body2">
                • This list shows people who have <strong>followed</strong> or <strong>interacted</strong> with your approved clubs.
              </Typography>
              <Typography variant="body2">
                • Use search, filters, and location to narrow down members.
              </Typography>
              <Typography variant="body2">
                • Click a user row to see which clubs they belong to, and export to CSV for offline analysis.
              </Typography>
            </Box>
          }
          arrow
        >
          <IconButton
            size="small"
            sx={{ color: isDark ? "#cbd5e1" : "#4a5568" }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </TouchFriendlyTooltip>
        <SearchInput
          value={filters.search}
          onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          onClear={() => setFilters((prev) => ({ ...prev, search: "" }))}
          placeholder="Search by name or email..."
          width={280}
          height="40px"
        />

        <ActionButton
          variant="secondary"
          startIcon={<FilterListIcon />}
          onClick={(e) => {
            e.currentTarget.blur(); // Remove focus from button before opening dialog
            setFilterDialogOpen(true);
          }}
        >
          Filters {hasActiveFilters && `(${Object.values(filters).filter(v => v && v !== "1000").length})`}
        </ActionButton>

        <ActionButton
          variant="secondary"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? 'Exporting...' : 'Export to CSV'}
        </ActionButton>
      </PageHeader>

      {/* Active Filters */}
      {hasActiveFilters && (
        <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {filters.role && (
            <Chip
              label={`Role: ${filters.role}`}
              onDelete={() => handleFilterChange('role', '')}
              sx={{
                backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                "& .MuiChip-deleteIcon": {
                  color: isDark ? "#818cf8" : "#1976d2",
                  "&:hover": { color: isDark ? "#a5b4fc" : "#1565c0" },
                },
              }}
            />
          )}
          {filters.status && (
            <Chip
              label={`Status: ${filters.status}`}
              onDelete={() => handleFilterChange('status', '')}
              sx={{
                backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                "& .MuiChip-deleteIcon": {
                  color: isDark ? "#818cf8" : "#1976d2",
                  "&:hover": { color: isDark ? "#a5b4fc" : "#1565c0" },
                },
              }}
            />
          )}
          {filters.sport_id && (
            <Chip
              label={`Sport: ${sportsList.find((s) => s.id.toString() === filters.sport_id)?.name || filters.sport_id}`}
              onDelete={() => {
                // Set to default sport instead of clearing
                const defaultSport = props?.props?.profile?.sportList?.[0]?.id ||
                                    props?.props?.profile?.sportIdList?.[0] ||
                                    sportsList[0]?.id ||
                                    "";
                handleFilterChange('sport_id', defaultSport ? defaultSport.toString() : '');
              }}
              sx={{
                backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                "& .MuiChip-deleteIcon": {
                  color: isDark ? "#818cf8" : "#1976d2",
                  "&:hover": { color: isDark ? "#a5b4fc" : "#1565c0" },
                },
              }}
            />
          )}
          {filters.sub_org_id && (
            <Chip
              label={`Club: ${subOrgsList.find((s) => s.id.toString() === filters.sub_org_id)?.name || filters.sub_org_id}`}
              onDelete={() => handleFilterChange('sub_org_id', '')}
              sx={{
                backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                "& .MuiChip-deleteIcon": {
                  color: isDark ? "#818cf8" : "#1976d2",
                  "&:hover": { color: isDark ? "#a5b4fc" : "#1565c0" },
                },
              }}
            />
          )}
          {filters.location && (
            <Chip
              label={`Location: ${filters.location}`}
              onDelete={() => {
                handleFilterChange('location', '');
                handleFilterChange('user_latitude', '');
                handleFilterChange('user_longitude', '');
              }}
              sx={{
                backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                "& .MuiChip-deleteIcon": {
                  color: isDark ? "#818cf8" : "#1976d2",
                  "&:hover": { color: isDark ? "#a5b4fc" : "#1565c0" },
                },
              }}
            />
          )}
        </Box>
      )}

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="md"
        fullWidth
        disableEnforceFocus={false}
        disableAutoFocus={false}
        disableRestoreFocus={false}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            margin: { xs: 2, sm: 3 },
            maxHeight: "85vh",
          },
        }}
      >
        <DialogTitle sx={{ color: isDark ? "#ffffff" : "#1a202c", pb: 2, px: { xs: 2, sm: 3 } }}>
          Filter Users
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={filters.role}
                  label="Role"
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  sx={{
                    color: isDark ? "#ffffff" : "#1a202c",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: isDark ? "rgba(255, 255, 255, 0.23)" : undefined,
                    },
                  }}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  <MenuItem value="coach">Coach</MenuItem>
                  <MenuItem value="player">Player</MenuItem>
                  <MenuItem value="parent">Parent</MenuItem>
                  <MenuItem value="referee">Referee</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  sx={{
                    color: isDark ? "#ffffff" : "#1a202c",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: isDark ? "rgba(255, 255, 255, 0.23)" : undefined,
                    },
                  }}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Sport</InputLabel>
                <Select
                  value={filters.sport_id}
                  label="Sport"
                  onChange={(e) => handleFilterChange('sport_id', e.target.value)}
                  sx={{
                    color: isDark ? "#ffffff" : "#1a202c",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: isDark ? "rgba(255, 255, 255, 0.23)" : undefined,
                    },
                  }}
                >
                  <MenuItem value="">All Sports</MenuItem>
                  {sportsList.map((sport) => (
                    <MenuItem key={sport.id} value={sport.id.toString()}>
                      {sport.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Club</InputLabel>
                <Select
                  value={filters.sub_org_id}
                  label="Club"
                  onChange={(e) => handleFilterChange('sub_org_id', e.target.value)}
                  sx={{
                    color: isDark ? "#ffffff" : "#1a202c",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: isDark ? "rgba(255, 255, 255, 0.23)" : undefined,
                    },
                  }}
                >
                  <MenuItem value="">All Clubs</MenuItem>
                  {subOrgsList.map((org) => (
                    <MenuItem key={org.id} value={org.id.toString()}>
                      {org.name || `Org #${org.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 1, color: isDark ? "#cbd5e1" : "#718096" }}>
                Location (Optional - enter location and radius to filter by distance)
              </Typography>
              <LocationSearch
                value={filters.location}
                onChange={handleLocationSelect}
                placeholder="Enter location..."
                name="location"
                style={{
                  backgroundColor: isDark ? "#242424" : "#f7fafc",
                  color: isDark ? "#ffffff" : "#1a202c",
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 2, gap: isMobile ? 1 : 0, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
          <Button
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
            fullWidth={isMobile}
            sx={{ color: isDark ? "#cbd5e1" : "#718096", minHeight: { xs: 44, sm: 'auto' } }}
          >
            Clear All
          </Button>
          <Button
            onClick={() => setFilterDialogOpen(false)}
            variant="contained"
            fullWidth={isMobile}
            sx={{
              backgroundColor: isDark ? "#6366f1" : "#1976d2",
              "&:hover": { backgroundColor: isDark ? "#818cf8" : "#1565c0" },
              minHeight: { xs: 44, sm: 'auto' },
            }}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      <Card noPadding>
        <DataTable
          columns={columns}
          data={users}
          loading={loading}
          pagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalUsers}
          onPageChange={(e, newPage) => {
            setPage(newPage);
          }}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          emptyMessage="No users found"
          onRowClick={(row) =>
            setUserDetailsModal({
              open: true,
              userId: row.id,
              role: row.role,
              locationLabel: "",
            })
          }
          mobileTitleColumn="name"
          mobileHiddenColumns={["id"]}
        />
      </Card>

      {/* Sports Dialog */}
      <Dialog
        open={sportsDialog.open}
        onClose={() => setSportsDialog({ open: false, sports: [], user: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            borderRadius: "16px",
            margin: { xs: 1, sm: 3 },
            maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
            width: { xs: 'calc(100% - 16px)', sm: '100%' },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            px: { xs: 2, sm: 3 },
            borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
            fontSize: "1rem",
            fontWeight: 600,
            color: isDark ? "#ffffff" : "#1a202c",
          }}
        >
          User Sports
        </DialogTitle>
        <DialogContent sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
          {sportsDialog.sports.length === 0 ? (
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: isDark ? "#cbd5e1" : "#718096",
              }}
            >
              No sports found.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {sportsDialog.sports.map((sport, index) => (
                <Typography
                  key={index}
                  sx={{
                    fontSize: "0.875rem",
                    color: isDark ? "#e2e8f0" : "#1a202c",
                  }}
                >
                  {sport}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{ p: { xs: 2, sm: 2 }, borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0" }}
        >
          <Button
            onClick={() => setSportsDialog({ open: false, sports: [], user: null })}
            sx={{ textTransform: "none", minHeight: { xs: 44, sm: 'auto' } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Details Modal (shared with admin) */}
      <UserDetailsModal
        open={userDetailsModal.open}
        onClose={() =>
          setUserDetailsModal({
            open: false,
            userId: null,
            role: null,
            locationLabel: "",
          })
        }
        userId={userDetailsModal.userId}
        role={userDetailsModal.role}
        locationLabel={userDetailsModal.locationLabel}
        token={token}
        setIsLoaded={props.setIsLoaded}
        hideEmail={!allowMemberEmails}
      />

      {/* Sub-Orgs Dialog */}
      <Dialog
        open={subOrgsDialog.open}
        onClose={() => setSubOrgsDialog({ open: false, user: null, subOrgs: [] })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            borderRadius: "16px",
            margin: { xs: 2, sm: 3 },
            maxHeight: "85vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            px: { xs: 2, sm: 3 },
            borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
          }}
        >
          <Typography sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, fontWeight: 700, color: isDark ? "#ffffff" : "#1a202c", mr: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            Clubs - {subOrgsDialog.user ? `${subOrgsDialog.user.first_name || ""} ${subOrgsDialog.user.last_name || ""}` : ""}
          </Typography>
          <IconButton
            onClick={() => setSubOrgsDialog({ open: false, user: null, subOrgs: [] })}
            sx={{ color: isDark ? "#cbd5e1" : "#718096", flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, px: { xs: 2, sm: 3 }, maxHeight: "70vh", overflowY: "auto" }}>
          {subOrgsDialog.subOrgs.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>No clubs found</Typography>
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              {subOrgsDialog.subOrgs.map((subOrg, index) => {
                const logoUrl = subOrg.logo_url || subOrg.logo || "";
                const orgName = subOrg.name || 
                               subOrg.organisation_name || 
                               subOrg.organization_name ||
                               subOrg.org_name ||
                               "";
                const orgEmail = subOrg.user?.email || 
                                subOrg.email || 
                                subOrg.contact_email ||
                                "";
                const orgType = formatClubType(subOrg.school_type || subOrg.type || "");
                
                if (!orgName) return null;
                
                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 2,
                      mb: 1,
                      borderRadius: "8px",
                      backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f7fafc",
                      border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
                    }}
                  >
                    <Avatar
                      src={logoUrl}
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                        color: isDark ? "#818cf8" : "#1976d2",
                        fontSize: "1.25rem",
                        fontWeight: 600,
                      }}
                    >
                      {orgName[0]?.toUpperCase() || "O"}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
                        {orgName}
                      </Typography>
                      {orgEmail && (
                        <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
                          {orgEmail}
                        </Typography>
                      )}
                      {orgType && orgType !== "-" && (
                        <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#cbd5e1" : "#718096", mt: 0.5 }}>
                          Club type: {orgType}
                        </Typography>
                      )}
                    </Box>
                    {subOrg.status && (
                      <StatusBadge status={subOrg.status} />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2 }, borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setSubOrgsDialog({ open: false, user: null, subOrgs: [] })}
            sx={{ textTransform: "none", minHeight: { xs: 44, sm: 'auto' } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersDirectory;
